import { useCallback, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import { getRelatorioSemanal } from "../../services/monitoramentoSerasa";
import type { RelatorioSemanalPayload } from "../../models/monitoramentoSerasa/types";
import { ApiErrorPanel, formatDataHora, PainelRodape } from "./shared";

export const MonitoramentoRelatorio = () => {
  const padraoInicio = moment()
    .startOf("isoWeek")
    .subtract(1, "week")
    .format("YYYY-MM-DD");
  const padraoFim = moment()
    .endOf("isoWeek")
    .subtract(1, "week")
    .format("YYYY-MM-DD");

  const [inicio, setInicio] = useState(padraoInicio);
  const [fim, setFim] = useState(padraoFim);
  const [payload, setPayload] = useState<RelatorioSemanalPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");

  const gerar = useCallback(async () => {
    setLoading(true);
    setErro("");
    setPayload(null);
    const periodoInicioUtc = `${inicio}T00:00:00.000Z`;
    const periodoFimUtc = `${fim}T23:59:59.999Z`;
    try {
      const p = await getRelatorioSemanal(periodoInicioUtc, periodoFimUtc);
      setPayload(p);
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "message" in e
          ? String((e as { message?: string }).message)
          : "Não foi possível obter o relatório.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  }, [inicio, fim]);

  const baixar = () => {
    if (!payload) return;
    if (payload.urlArquivo) {
      window.open(payload.urlArquivo, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" fontWeight={700} gutterBottom>
        Relatório semanal
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Parâmetros <code>periodoInicioUtc</code> e <code>periodoFimUtc</code> (API Operação).
      </Typography>

      {erro ? (
        <Box sx={{ mb: 2 }}>
          <ApiErrorPanel message={erro} onRetry={gerar} />
        </Box>
      ) : null}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
            <TextField
              size="small"
              type="date"
              label="Data início"
              InputLabelProps={{ shrink: true }}
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
            <TextField
              size="small"
              type="date"
              label="Data fim"
              InputLabelProps={{ shrink: true }}
              value={fim}
              onChange={(e) => setFim(e.target.value)}
            />
            <Button variant="contained" onClick={gerar} disabled={loading}>
              {loading ? "Gerando…" : "Buscar relatório"}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {payload ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Período (UTC)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {payload.periodoInicioUtc
                ? formatDataHora(payload.periodoInicioUtc)
                : inicio}{" "}
              —{" "}
              {payload.periodoFimUtc ? formatDataHora(payload.periodoFimUtc) : fim}
            </Typography>
            {payload.quantidadeCnpjsComSnapshot != null ? (
              <Typography variant="body2" sx={{ mb: 1 }}>
                CNPJs com snapshot: <strong>{payload.quantidadeCnpjsComSnapshot}</strong>
              </Typography>
            ) : null}
            {payload.versaoRegras != null ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Versão das regras: <strong>{String(payload.versaoRegras)}</strong>
              </Typography>
            ) : null}
            {payload.urlArquivo ? (
              <Button startIcon={<DownloadIcon />} variant="outlined" onClick={baixar}>
                Abrir arquivo ({payload.nomeArquivo || "download"})
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                A API não retornou <code>urlArquivo</code>.
              </Typography>
            )}
          </CardContent>
        </Card>
      ) : null}

      <PainelRodape
        versaoRegras={payload?.versaoRegras}
        labelDadosAte={
          payload?.geradoEmUtc
            ? `Gerado em ${formatDataHora(payload.geradoEmUtc)}`
            : undefined
        }
      />
    </Box>
  );
};
